import { useMemo, useState, type ReactNode } from "react";
import type { ClubCrest, ClubKit, EntradaDeClubeCriado } from "@legacy-manager/engine";

import { avaliarClubeCriado, normalizarSigla, sugerirSiglas } from "../game/clube-criado";
import { useT } from "../i18n";
import { CriadorDeEscudo } from "../ui/brasoes/CriadorDeEscudo";
import { EditorDeUniforme } from "../ui/brasoes/EditorDeUniforme";
import { Panel } from "../ui/atoms";

/**
 * Criar o clube da carreira: identidade, escudo e uniforme.
 *
 * Esta tela só junta as peças e decide quando o botão age. Quem desenha escudo
 * e uniforme são os dois editores; quem julga nome e sigla é `clube-criado`.
 *
 * **A sigla é conferida contra o mundo inteiro, e não contra a divisão.** São
 * 320 clubes gerados, e duas siglas iguais na mesma temporada confundem tabela,
 * mercado e notícia, mesmo vindo de divisões diferentes.
 *
 * **Confirmar é definitivo**, e a tela não esconde isso: o escudo não muda
 * depois de criado, por decisão de produto, e é por isso que a prévia nos três
 * tamanhos fica à vista o tempo todo em vez de aparecer só no fim.
 */

/** O escudo com que a tela abre, para nunca existir desenho vazio. */
function escudoInicial(): ClubCrest {
  return {
    shape: "shield",
    pattern: "solid",
    primary: "#1b5e3a",
    secondary: "#ffffff",
    detail: "#f2c200",
    pieces: [],
  };
}

function uniformeInicial(escudo: ClubCrest): ClubKit {
  return {
    pattern: "solid",
    primary: escudo.primary,
    secondary: escudo.secondary,
    badge: null,
  };
}

type Aba = "escudo" | "uniforme";

/**
 * O recado de um campo, que muda de tom conforme o que ele é.
 *
 * Campo em branco recebe dica apagada, e não erro em vermelho: a tela abria com
 * "Três letras, de A a Z." pintado de vermelho antes de alguém ter digitado uma
 * letra, o que lê como se a pessoa já tivesse errado alguma coisa. Vermelho
 * fica para o que foi escrito e não serve, e `role="alert"` junto, porque só aí
 * existe algo que precisa interromper quem usa leitor de tela.
 */
function Aviso({
  texto,
  vazio,
  aceito,
}: {
  texto: string;
  vazio: boolean;
  aceito: boolean;
}): ReactNode {
  const cor = vazio ? "var(--text-dim)" : aceito ? "var(--warning)" : "var(--danger)";
  return (
    <span {...(vazio ? {} : { role: "alert" })} style={{ fontSize: 11.5, color: cor }}>
      {texto}
    </span>
  );
}

export function CriarClube({
  countryId,
  siglasDoMundo,
  busy,
  aoCriar,
  aoVoltar,
}: {
  countryId: string;
  /** Todas as siglas que já existem no mundo gerado. */
  siglasDoMundo: string[];
  busy: boolean;
  /*
   * O fundador não vem daqui.
   *
   * Esta tela cuida da identidade do clube: nome, sigla, escudo e uniforme.
   * Quem está fundando é o técnico da carreira, e quem sabe disso é quem
   * comandou a criação. Pedir o nome de novo aqui seria perguntar duas vezes.
   */
  aoCriar: (entrada: Omit<EntradaDeClubeCriado, "founder">) => void;
  aoVoltar: () => void;
}): ReactNode {
  const t = useT();
  const [nome, definirNome] = useState("");
  const [sigla, definirSigla] = useState("");
  const [escudo, definirEscudo] = useState<ClubCrest>(escudoInicial);
  const [uniforme, definirUniforme] = useState<ClubKit>(() => uniformeInicial(escudoInicial()));
  const [aba, definirAba] = useState<Aba>("escudo");

  const exame = useMemo(
    () => avaliarClubeCriado(nome, sigla, siglasDoMundo),
    [nome, sigla, siglasDoMundo],
  );

  /*
   * As sugestões saem do nome, e só quando a sigla ainda não serve.
   *
   * Oferecer alternativa para quem já digitou uma sigla livre seria empurrar
   * troca sem motivo. Elas aparecem quando a escolhida esbarra em alguma coisa,
   * que é quando a pessoa precisa de saída e não de opinião.
   */
  const sugestoes = useMemo(() => {
    if (exame.sigla.aceito || nome.trim().length < 2) return [];
    return sugerirSiglas(nome, siglasDoMundo);
  }, [exame.sigla.aceito, nome, siglasDoMundo]);

  function confirmar(): void {
    if (!exame.podeCriar || busy) return;
    aoCriar({
      countryId,
      name: nome.trim(),
      short: normalizarSigla(sigla),
      crest: escudo,
      kit: uniforme,
    });
  }

  return (
    /*
      A moldura que faltava.

      Sem ela o criador nascia colado nas duas bordas do monitor, com os painéis
      de ponta a ponta e nada de recuo, enquanto a escolha de clube, de onde ele
      sai, já vinha centrada em mil.

      São 932 e não mil porque quem manda na largura aqui é o editor: 900 de
      corpo mais os dois recuos de 16. Assim o painel do nome termina na mesma
      linha que a coluna dos controles, em vez de passar dela em setenta pixels.
    */
    <div
      className="grid"
      style={{
        gap: 14,
        alignContent: "start",
        maxWidth: 932,
        margin: "0 auto",
        padding: "28px var(--gutter) 40px",
        width: "100%",
      }}
    >
      <Panel title={t("criador.titulo")}>
        <div
          className="grid"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}
        >
          <label className="grid" style={{ gap: 4 }}>
            <span className="label-muted">{t("criador.nome.rotulo")}</span>
            <input
              value={nome}
              onChange={(evento) => definirNome(evento.target.value)}
              maxLength={40}
              autoComplete="off"
            />
            {exame.nome.chave && (
              <Aviso
                texto={t(exame.nome.chave)}
                vazio={nome.trim().length === 0}
                aceito={exame.nome.aceito}
              />
            )}
          </label>

          <label className="grid" style={{ gap: 4 }}>
            <span className="label-muted">{t("criador.sigla.rotulo")}</span>
            <input
              value={sigla}
              onChange={(evento) => definirSigla(normalizarSigla(evento.target.value))}
              maxLength={3}
              autoComplete="off"
              /* Três letras não precisam de meia tela de campo. */
              style={{
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                width: "min(140px, 100%)",
              }}
            />
            {exame.sigla.chave && (
              <Aviso
                texto={t(exame.sigla.chave)}
                vazio={sigla.length === 0}
                aceito={exame.sigla.aceito}
              />
            )}
            {sugestoes.length > 0 && (
              <span className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                <span className="label-muted">{t("criador.sigla.sugestoes")}</span>
                {sugestoes.map((livre) => (
                  <button
                    key={livre}
                    className="ghost fixo"
                    onClick={() => definirSigla(livre)}
                    style={{ minHeight: 26, padding: "0 9px", fontSize: 12, fontWeight: 700 }}
                  >
                    {livre}
                  </button>
                ))}
              </span>
            )}
          </label>
        </div>
      </Panel>

      {/* Escudo e uniforme dividem a tela por aba: os dois juntos não cabem num
          celular, e quem está desenhando um não está olhando o outro. */}
      <div className="row" style={{ gap: 8 }}>
        {(["escudo", "uniforme"] as Aba[]).map((qual) => (
          <button
            key={qual}
            className="ghost"
            aria-pressed={aba === qual}
            onClick={() => definirAba(qual)}
            style={
              aba === qual
                ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                : undefined
            }
          >
            {qual === "escudo" ? t("criador.escudo") : t("criador.uniforme.titulo")}
          </button>
        ))}
      </div>

      {aba === "escudo" ? (
        <CriadorDeEscudo
          escudo={escudo}
          sigla={normalizarSigla(sigla) || "?"}
          aoMudar={definirEscudo}
        />
      ) : (
        <EditorDeUniforme uniforme={uniforme} aoMudar={definirUniforme} />
      )}

      <div className="row" style={{ gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button className="ghost" onClick={aoVoltar} disabled={busy}>
          {t("comum.voltar")}
        </button>
        <button className="primary" onClick={confirmar} disabled={!exame.podeCriar || busy}>
          {t("criador.confirmar")}
        </button>
      </div>
    </div>
  );
}
